/* Aktion-Overlay
 *
 * Hängt Badges an ShopPad Infinite-Options-Swatches, wenn der Option-Wert
 * in einer aktiven Aktion vorkommt. Liest URL-Param (z.B. ?netzfarbe=rot)
 * und selektiert den passenden Swatch vor.
 *
 * Die App rendert Swatches clientseitig — daher MutationObserver statt
 * einmaligem DOM-Scan. Match via aria-label, title, data-attributes oder
 * innerText (Fallback-Kaskade).
 *
 * Debug: URL-Param ?aktion-debug=1 loggt Match-Versuche in die Konsole.
 */
(function () {
  'use strict';

  const DEBUG = new URLSearchParams(window.location.search).has('aktion-debug');
  const log = DEBUG ? console.log.bind(console, '[aktion]') : function () {};

  // --- Daten aus dem vom Theme gerenderten Script-Tag laden ----------------
  const dataNode = document.querySelector('script[data-aktion-data]');
  if (!dataNode) {
    log('Kein aktion-data-Script auf dieser Seite. Abbruch.');
    return;
  }

  let aktionen = [];
  try {
    aktionen = JSON.parse(dataNode.textContent);
  } catch (err) {
    console.error('[aktion] JSON-Parse-Fehler:', err);
    return;
  }
  if (!aktionen.length) return;

  // Labels in der App haben oft einen Preis-Suffix, z.B. "Royalrot (+ 50 €)".
  // Metaobject speichert den kurzen Namen "Royalrot". Für den Match
  // normalisieren wir beide Seiten: lowercase + Preis-Suffix abschneiden.
  const PRICE_SUFFIX_RE = /\s*\(\s*[+\-]?\s*[\d.,]+\s*[€$]\s*\)\s*$/;
  function normalize(label) {
    return String(label || '')
      .replace(PRICE_SUFFIX_RE, '')
      .toLowerCase()
      .trim();
  }

  // Flache Map: normalisierter option_wert → { label, badge_text, badge_farbe, original_preis }
  const optionMap = new Map();
  for (const a of aktionen) {
    const werte = Array.isArray(a.option_werte) ? a.option_werte : [];
    const preise = a.original_preise || {};
    for (const wert of werte) {
      optionMap.set(normalize(wert), {
        label: wert,
        badge_text: a.badge_text,
        badge_farbe: a.badge_farbe || '#e2001a',
        original_preis: preise[wert] || null,
      });
    }
  }
  log('Geladene Option-Werte (normalisiert):', Array.from(optionMap.keys()));

  // --- Swatch-Finder: mehrere Strategien ----------------------------------
  function extractLabel(el) {
    if (!el) return '';
    return (
      el.getAttribute('aria-label') ||
      el.getAttribute('title') ||
      el.getAttribute('data-option-value') ||
      el.getAttribute('data-value') ||
      el.getAttribute('data-title') ||
      (el.textContent || '').trim()
    );
  }

  function findSwatchHost(el) {
    // Gehe hoch bis zu einem Element, das als positionsbarer Container taugt
    // (typischerweise <label>, <button>, oder <div class*="option">)
    let cur = el;
    for (let i = 0; i < 4 && cur; i++) {
      const tag = cur.tagName && cur.tagName.toLowerCase();
      if (tag === 'label' || tag === 'button' || tag === 'li') return cur;
      if (cur.className && /option|swatch|choice/i.test(cur.className)) return cur;
      cur = cur.parentElement;
    }
    return el;
  }

  function badgeFor(entry) {
    const badge = document.createElement('span');
    badge.className = 'aktion-swatch-badge';
    badge.style.setProperty('--aktion-color', entry.badge_farbe);
    badge.textContent = entry.badge_text;
    badge.setAttribute('data-aktion-badge', entry.label);
    return badge;
  }

  // --- Aufpreis-Erkennung -------------------------------------------------
  // Der Rabatt bezieht sich auf den FARBAUFPREIS. Eine Option ohne Aufpreis
  // (z.B. Strebenfarbe „Weiß") darf KEIN Badge bekommen, auch wenn ihr Name
  // zufällig mit einer betroffenen Netzfarbe („Weiß") übereinstimmt.
  // Aufpreis-Quelle ist der ShopPad-Option-Value mit "(+ X €)"-Suffix — dieselbe
  // Quelle, die option-price-filter.js für die Tier-Pills nutzt. Standardfarben
  // (0 €) haben kein Suffix.
  const SURCHARGE_RE = /\(\s*\+\s*\d+\s*[€$]\s*\)/;
  function hasSurcharge(el) {
    const wrapper = (el.closest && el.closest('.spb-productoptionswatchwrapper')) || el;
    // 1. Zuverlässigste Quelle: der ShopPad-Input-Value (von keinem Script verändert).
    const input = wrapper.querySelector && wrapper.querySelector('input');
    if (input && SURCHARGE_RE.test(input.value || '')) return true;
    // 2. Theme-Aufpreis-Span (option-price-suffix.js trennt "(+ X €)" ab).
    if (wrapper.querySelector && wrapper.querySelector('.sp-price-suffix')) return true;
    // 3. Fallback: sichtbarer Aufpreis-Text irgendwo im Swatch.
    return SURCHARGE_RE.test(wrapper.textContent || '');
  }

  // --- Scannt einen Sub-Tree auf passende Swatches ------------------------
  function scanAndTag(root) {
    // Alle plausiblen Swatch-Kandidaten im Subtree
    const candidates = root.querySelectorAll(
      'label[class*="option"], label[class*="swatch"], button[class*="option"],' +
        '[class*="infiniteoption"] label, [class*="infinite-option"] label,' +
        '[class*="swatch"] label, [data-option-value], [data-value]'
    );

    candidates.forEach((el) => {
      const label = extractLabel(el);
      if (!label) return;
      const entry = optionMap.get(normalize(label));
      if (!entry) return;

      // Nur Swatches mit echtem Farbaufpreis taggen — sonst landet das Badge
      // auch auf gleichnamigen Optionen OHNE Aufpreis (z.B. Strebenfarbe „Weiß").
      if (!hasSurcharge(el)) {
        log('Skip — kein Aufpreis:', label);
        return;
      }

      const host = findSwatchHost(el);
      // Skippe, wenn schon ein Badge hängt
      if (host.querySelector(':scope > .aktion-swatch-badge')) return;

      // Sicherstellen, dass der Host positioniert ist
      const cs = window.getComputedStyle(host);
      if (cs.position === 'static') host.classList.add('aktion-swatch-host');

      host.appendChild(badgeFor(entry));
      log('Badge gesetzt auf', label, host);
    });
  }

  // --- Initial-Scan + MutationObserver (App rendert lazy) -----------------
  scanAndTag(document.body);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        scanAndTag(node);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // --- URL-Param-Vorauswahl ----------------------------------------------
  // Format: ?netzfarbe=rot → findet Swatch mit Label „rot" und klickt ihn
  const params = new URLSearchParams(window.location.search);
  const preselectKeys = ['netzfarbe', 'strebenfarbe', 'nahtfarbe', 'option'];
  const preselectTargets = [];
  for (const key of preselectKeys) {
    const val = params.get(key);
    if (val) preselectTargets.push(val.toLowerCase());
  }

  if (preselectTargets.length) {
    log('URL-Preselect-Ziele:', preselectTargets);

    function tryPreselect(target) {
      // Suche alle Elemente mit matchendem Label im gesamten Dokument
      const all = document.querySelectorAll('label, button, [data-option-value], [data-value]');
      const targetNorm = normalize(target);
      for (const el of all) {
        const label = extractLabel(el);
        if (!label) continue;
        if (normalize(label) === targetNorm) {
          log('Preselect-Click auf', label, el);
          // Click auf das tatsächliche Input falls vorhanden (Label) sonst auf Element
          const input = el.querySelector('input') || (el.getAttribute('for') && document.getElementById(el.getAttribute('for')));
          if (input && typeof input.click === 'function') {
            input.click();
          } else {
            el.click();
          }
          return true;
        }
      }
      return false;
    }

    let attempts = 0;
    const maxAttempts = 20; // ~10 Sekunden (20 × 500ms) für lazy App-Render
    const preselectInterval = setInterval(() => {
      attempts++;
      const unresolved = preselectTargets.filter((t) => !tryPreselect(t));
      if (unresolved.length === 0 || attempts >= maxAttempts) {
        clearInterval(preselectInterval);
        if (unresolved.length) log('Preselect aufgegeben nach', attempts, 'Versuchen. Offen:', unresolved);
      }
    }, 500);
  }
})();
